import React, { useEffect, useMemo, useState } from 'react';
import { Card, Row, Col, Statistic, Table, Tag, Space, Typography, Divider, Button, Segmented, message } from 'antd';
import apiService from "@/services/apiService.ts";

const HKTVmallDashboards: React.FC = () => {
  const [orders, setOrders] = useState<any[]>([]);
  const [outbounds, setOutbounds] = useState<any[]>([]);
  const [orderDateMode, setOrderDateMode] = useState<'order' | 'pickup' | 'delivery'>('order');
  const [weekRange, setWeekRange] = useState<'current' | 'previous'>('current');
  const [rangeDateMode, setRangeDateMode] = useState<'order' | 'pickup' | 'delivery'>('order');
  // 各商戶已拉取運單數（僅前端 mock）
  const [merchantViewDays, setMerchantViewDays] = useState<number>(7);

  const { CheckableTag } = Tag;

  useEffect(() => {
    // TODO: 換成實際 API：/sales/orders?hktvmall=1、/outbound?...
    const startOfWeek = new Date();
    startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());
    startOfWeek.setHours(0, 0, 0, 0);

    const format = (date: Date) => new Date(date).toISOString();
    const shift = (base: Date, days: number) => {
      const next = new Date(base);
      next.setDate(next.getDate() + days);
      return next;
    };

    const currentWeekMonday = shift(startOfWeek, 1);
    const previousWeekMonday = shift(startOfWeek, -6);
    const beforePreviousWeekMonday = shift(startOfWeek, -13);

    setOrders([
      {
        id: 'SO-001',
        amount: 1000,
        status: 'CONFIRMED',
        orderDate: format(currentWeekMonday),
        pickUpDate: format(shift(currentWeekMonday, 2)),
        deliveryDate: format(shift(currentWeekMonday, 3)),
      },
      {
        id: 'SO-002',
        amount: 800,
        status: 'CONFIRMED',
        orderDate: format(previousWeekMonday),
        pickUpDate: format(shift(previousWeekMonday, 2)),
        deliveryDate: format(shift(previousWeekMonday, 4)),
      },
      {
        id: 'SO-003',
        amount: 920,
        status: 'CONFIRMED',
        orderDate: format(beforePreviousWeekMonday),
        pickUpDate: format(shift(beforePreviousWeekMonday, 1)),
        deliveryDate: format(shift(beforePreviousWeekMonday, 3)),
      },
    ]);
    setOutbounds([
      {
        id: 'OB-001',
        qty: 20,
        status: 'PENDING',
        orderDate: format(currentWeekMonday),
        pickUpDate: format(shift(currentWeekMonday, 1)),
        deliveryDate: format(shift(currentWeekMonday, 2)),
      },
      {
        id: 'OB-002',
        qty: 10,
        status: 'SHIPPED',
        orderDate: format(previousWeekMonday),
        pickUpDate: format(shift(previousWeekMonday, 1)),
        deliveryDate: format(shift(previousWeekMonday, 3)),
      },
      {
        id: 'OB-003',
        qty: 16,
        status: 'SHIPPED',
        orderDate: format(beforePreviousWeekMonday),
        pickUpDate: format(shift(beforePreviousWeekMonday, 2)),
        deliveryDate: format(shift(beforePreviousWeekMonday, 4)),
      },
    ]);
  }, []);

  const soAmount = useMemo(()=> orders.reduce((s,o)=>s+o.amount,0), [orders]);
  const shipped = useMemo(()=> outbounds.filter(o=>o.status==='SHIPPED').length, [outbounds]);
  const pending = useMemo(()=> outbounds.filter(o=>o.status==='PENDING').length, [outbounds]);
  const weekLabels = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
  // mock weekly stats（本週/上週，拆分為 WMS/其他）
  const weeklyPreset = useMemo(() => ({
    order: {
      current: {
        thisLabel: '本週 - 其他',
        lastLabel: '上週 - 其他',
        thisOther: [7, 10, 16, 12, 16, 18, 20],
        lastOther: [6, 8, 14, 10, 14, 16, 18]
      },
      previous: {
        thisLabel: '上週 - 其他',
        lastLabel: '前週 - 其他',
        thisOther: [6, 8, 14, 10, 14, 16, 18],
        lastOther: [5, 7, 11, 9, 12, 14, 16]
      }
    },
    pickup: {
      current: {
        thisLabel: '本週 - 取貨',
        lastLabel: '上週 - 取貨',
        thisOther: [6, 10, 12, 14, 15, 17, 19],
        lastOther: [5, 7, 10, 11, 12, 13, 14]
      },
      previous: {
        thisLabel: '上週 - 取貨',
        lastLabel: '前週 - 取貨',
        thisOther: [5, 7, 10, 11, 12, 13, 14],
        lastOther: [4, 6, 9, 10, 11, 12, 12]
      }
    },
    delivery: {
      current: {
        thisLabel: '本週 - 派送',
        lastLabel: '上週 - 派送',
        thisOther: [8, 11, 13, 16, 17, 19, 21],
        lastOther: [7, 9, 11, 12, 13, 15, 16]
      },
      previous: {
        thisLabel: '上週 - 派送',
        lastLabel: '前週 - 派送',
        thisOther: [7, 9, 11, 12, 13, 15, 16],
        lastOther: [6, 8, 10, 11, 12, 13, 14]
      }
    }
  }), []);

  const { weeklyThisOther, weeklyLastOther, weeklyLegend } = useMemo(() => {
    const weeklyByMode = weeklyPreset[orderDateMode] as Record<'current' | 'previous', {
      thisLabel: string;
      lastLabel: string;
      thisOther: number[];
      lastOther: number[];
    }> | undefined;

    const active = weeklyByMode?.[weekRange] || weeklyByMode?.current;

    if (!active) {
      return {
        weeklyThisOther: [],
        weeklyLastOther: [],
        weeklyLegend: {
          thisLabel: '本週 - 其他',
          lastLabel: '上週 - 其他'
        }
      };
    }

    return {
      weeklyThisOther: active.thisOther,
      weeklyLastOther: active.lastOther,
      weeklyLegend: {
        thisLabel: active.thisLabel,
        lastLabel: active.lastLabel
      }
    };
  }, [weeklyPreset, orderDateMode, weekRange]);

  const weeklyMax = useMemo(() => Math.max(
    ...weeklyThisOther,
    ...weeklyLastOther,
  ) || 1, [weeklyThisOther, weeklyLastOther]);

  // 日期範圍統計（最近7/14/30天）
  const [rangeDays, setRangeDays] = useState<number>(7);
  const rangeLabels = useMemo(()=>{
    const arr: string[] = [];
    for(let i=rangeDays-1;i>=0;i--){
      const d = new Date(); d.setDate(d.getDate()-i);
      arr.push(`${d.getMonth()+1}/${d.getDate()}`);
    }
    return arr;
  },[rangeDays]);
  const rangeCountPresets = useMemo(() => ({
    order: [8, 12, 16, 20, 18],
    pickup: [6, 10, 14, 12, 16],
    delivery: [10, 14, 18, 16, 22]
  }), []);
  const rangeCounts = useMemo(()=> {
    const preset = rangeCountPresets[rangeDateMode] || rangeCountPresets.order;
    return rangeLabels.map((_,i)=> preset[i % preset.length]);
  }, [rangeLabels, rangeCountPresets, rangeDateMode]);
  const rangeMax = useMemo(() => Math.max(...rangeCounts,1), [rangeCounts]);
  const filteredOrders = useMemo(() => {
    if (!Array.isArray(orders)) return [];
    const field = orderDateMode === 'order' ? 'orderDate' : orderDateMode === 'pickup' ? 'pickUpDate' : 'deliveryDate';
    const candidate = orders.filter((o) => o[field]);
    if (!candidate.length) return orders;

    const now = new Date();
    const start = new Date(now);
    start.setDate(start.getDate() - start.getDay());
    start.setHours(0, 0, 0, 0);
    if (weekRange === 'previous') {
      start.setDate(start.getDate() - 7);
    }
    const end = new Date(start);
    end.setDate(end.getDate() + 7);

    const filtered = candidate.filter((o) => {
      const raw = o[field];
      if (!raw) return false;
      const d = new Date(raw);
      if (Number.isNaN(d.getTime())) return false;
      return d >= start && d < end;
    });

    return filtered.length ? filtered : candidate;
  }, [orders, orderDateMode, weekRange]);

  const filteredOutbounds = useMemo(() => {
    if (!Array.isArray(outbounds)) return [];
    const field = rangeDateMode === 'order' ? 'orderDate' : rangeDateMode === 'pickup' ? 'pickUpDate' : 'deliveryDate';
    const candidate = outbounds.filter((o) => o[field]);
    return candidate.length ? candidate : outbounds;
  }, [outbounds, rangeDateMode]);


  // Mock 資料：各商戶在不同日範圍的已拉取運單數
  const [merchantStats, setMerchantStats] = useState<Array<{ storeCode: string; orderCount: number }>>([]);
  useEffect(() => {
    const ctrl = new AbortController();
    const load = async () => {
      try {
        const res = await apiService.get<{
          data: Array<{ storeCode: string; orderCount: number }>;
          success: boolean;
          message?: string
        }>('/integrations/hktvmall/merchant-stats', {
          params: { days: merchantViewDays }
        }, {
          signal: ctrl.signal
        });

        const { data, success, message: msg } = res;

        if (!success) throw new Error(msg || 'Failed fetching merchant stats');
        setMerchantStats(Array.isArray(data) ? data : []);
      } catch (e) {
        if ((e as any)?.name !== 'AbortError') {
          console.error('Failed to fetch merchant stats', e);
          message.error('無法獲取商戶統計');
          setMerchantStats([]);
        }
      }
    };
    load();
    return () => ctrl.abort();
  }, [merchantViewDays]);

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>

      {/* 頂部功能列已移除（按需求隱藏） */}

      <Row gutter={[16, 16]}>
        <Col xs={24} md={6}>
          <Card>
            <Statistic title="月度銷售額 (USD)" value={soAmount} precision={2} suffix={<span style={{ color:'#52c41a', fontSize:12, marginLeft:8 }}>↑ +2.5%</span>} />
          </Card>
        </Col>
        <Col xs={24} md={6}>
          <Card>
            <Statistic title="訂單數" value={orders.length} suffix={<span style={{ color:'#1677ff', fontSize:12, marginLeft:8 }}>+12</span>} />
          </Card>
        </Col>
        <Col xs={24} md={6}>
          <Card>
            <Statistic title="已出庫" value={shipped} suffix={<span style={{ color:'#52c41a', fontSize:12, marginLeft:8 }}>✔</span>} />
          </Card>
        </Col>
        <Col xs={24} md={6}>
          <Card>
            <Statistic title="待出庫" value={pending} suffix={<span style={{ color:'#faad14', fontSize:12, marginLeft:8 }}>…</span>} />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16,16]}>
        <Col xs={24} md={12}>
          <Card title={<Space>📊 訂單統計 <Segmented size="small" options={[{label:'訂單日期',value:'order'},{label:'取貨日期',value:'pickup'},{label:'派送日期',value:'delivery'}]} value={orderDateMode} onChange={(v)=> setOrderDateMode(v as 'order'|'pickup'|'delivery')} /></Space>}>
            {/* 左側：每週統計（本週 vs 上週，WMS/其他） */}
            <div style={{ padding: '8px 8px 0', background: 'linear-gradient(180deg,#f0f5ff,#fff)', borderRadius: 8 }}>
              <div style={{ display:'flex', gap: 8, alignItems:'flex-end', height: 180 }}>
                {weekLabels.map((w,i)=> (
                  <div key={i} style={{ flex:1, textAlign:'center' }}>
                    <div style={{ display:'flex', alignItems:'flex-end', justifyContent:'center', gap: 3, height: 150 }}>
                      {/*<div title={`上週-WMS ${weeklyLastWMS[i]}`} style={{ width: 8, height: `${Math.round(100*weeklyLastWMS[i]/weeklyMax)}%`, background:'#95de64', borderRadius: 4 }} />*/}
                      <div title={`上週-其他 ${weeklyLastOther[i]}`} style={{ width: 8, height: `${Math.round(100*weeklyLastOther[i]/weeklyMax)}%`, background:'#95de64', borderRadius: 4 }} />
                     {/* <div title={`本週-WMS ${weeklyThisWMS[i]}`} style={{ width: 8, height: `${Math.round(100*weeklyThisWMS[i]/weeklyMax)}%`, background:'#69c0ff', borderRadius: 4 }} />*/}
                      <div title={`本週-其他 ${weeklyThisOther[i]}`} style={{ width: 8, height: `${Math.round(100*weeklyThisOther[i]/weeklyMax)}%`, background:'#69c0ff', borderRadius: 4 }} />
                    </div>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>{w}</Typography.Text>
                  </div>
                ))}
              </div>
              <Space size={16} style={{ marginTop: 8 }}>
                <CheckableTag
                  checked={weekRange === 'previous'}
                  onChange={() => setWeekRange('previous')}
                  style={{ borderRadius: 4, padding: '2px 10px' }}
                >
                  {weeklyLegend.lastLabel}
                </CheckableTag>
                <CheckableTag
                  checked={weekRange === 'current'}
                  onChange={() => setWeekRange('current')}
                  style={{ borderRadius: 4, padding: '2px 10px' }}
                >
                  {weeklyLegend.thisLabel}
                </CheckableTag>
              </Space>
            </div>
            <Divider style={{ margin: 12 }} />
            <Table size="small" rowKey="id" dataSource={filteredOrders} pagination={false}
              columns={[{ title: 'SO No', dataIndex: 'id', width: 160 }, { title: 'Amount', dataIndex: 'amount', width: 120 }, { title: 'Status', dataIndex: 'status', render:(v)=> <Tag color={v==='CONFIRMED'?'green':'default'}>{v}</Tag> }]} />
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card title={<Space>📈 按日期範圍統計 <Segmented size="small" options={[{label:'訂單日期',value:'order'},{label:'取貨日期',value:'pickup'},{label:'派送日期',value:'delivery'}]} value={rangeDateMode} onChange={(v)=> setRangeDateMode(v as 'order'|'pickup'|'delivery')} /> <Segmented size="small" options={[{label:'最近7天',value:7},{label:'最近14天',value:14},{label:'最近30天',value:30}]} value={rangeDays} onChange={(v)=> setRangeDays(Number(v))} /></Space>}>
            {/* 右側：日期範圍柱狀圖（mock） */}
            <div style={{ padding: '8px 8px 0', background: 'linear-gradient(180deg,#fffbe6,#fff)', borderRadius: 8 }}>
              <div style={{ display:'flex', gap: 8, alignItems:'flex-end', height: 180 }}>
                {rangeLabels.map((d,i)=> (
                  <div key={i} style={{ flex:1, textAlign:'center' }}>
                    <div style={{ display:'flex', alignItems:'flex-end', justifyContent:'center', height: 150 }}>
                      <div title={`${d}: ${rangeCounts[i]}`} style={{ width: 12, height: `${Math.round(100*rangeCounts[i]/rangeMax)}%`, background:'#ffd666', borderRadius: 4 }} />
                    </div>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>{d}</Typography.Text>
                  </div>
                ))}
              </div>
            </div>
            <Divider style={{ margin: 12 }} />
            <Table size="small" rowKey="id" dataSource={filteredOutbounds} pagination={false}
              columns={[{ title: 'OB No', dataIndex: 'id', width: 160 }, { title: 'Qty', dataIndex: 'qty', width: 120 }, { title: 'Status', dataIndex: 'status', render:(v)=> <Tag color={v==='SHIPPED'?'blue':'orange'}>{v}</Tag> }]} />
          </Card>
        </Col>
      </Row>

      {/* 第二行：各商戶已拉取運單數 - 全寬 */}
      <Row gutter={[16,16]}>
        <Col xs={24}>
          <Card 
            title={
              <Space>
                <span>各商戶運單拉取統計</span>
                <Segmented 
                  size="small" 
                  options={[
                    {label:'最近7天',value:7},
                    {label:'最近14天',value:14},
                    {label:'最近30天',value:30}
                  ]} 
                  value={merchantViewDays} 
                  onChange={(v)=> setMerchantViewDays(Number(v))} 
                />
              </Space>
            }
            extra={
              <Space>
                <span style={{ fontSize: 14, color: '#666' }}>
                  共 {merchantStats.length} 個商戶
                </span>
              </Space>
            }
          >
            <Table 
              size="small" 
              rowKey={(r)=> r.storeCode} 
              dataSource={merchantStats} 
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => `第 ${range[0]}-${range[1]} 項，共 ${total} 個商戶`,
                pageSizeOptions: ['10', '20', '50'],
                size: 'small'
              }}
              tableLayout="fixed"
              columns={[
                { 
                  title: 'Store Code', 
                  dataIndex: 'storeCode', 
                  width: 160,
                  render: (text) => (
                    <span style={{ fontWeight: 500, color: '#1890ff' }}>
                      {text}
                    </span>
                  )
                },
                { 
                  title: '運單數', 
                  dataIndex: 'orderCount', 
                  width: 120,
                  align: 'left' as const,
                  render: (value) => (
                    <span style={{ 
                      fontSize: 16, 
                      fontWeight: 'bold', 
                      color: '#1a1a1a' 
                    }}>
                      {value}
                    </span>
                  )
                },
                {
                  title: '佔比',
                  width: 260,
                  render: (_, record) => {
                    const maxCount = Math.max(...merchantStats.map(m => m.orderCount));
                    const percentage = ((record.orderCount / maxCount) * 100).toFixed(1);
                    return (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ 
                          flex: 1, 
                          height: 6, 
                          background: '#f0f0f0', 
                          borderRadius: 3,
                          overflow: 'hidden'
                        }}>
                          <div style={{ 
                            width: `${percentage}%`, 
                            height: '100%', 
                            background: '#1890ff',
                            transition: 'width 0.3s ease'
                          }} />
                        </div>
                        <span style={{ fontSize: 12, color: '#666', minWidth: 35 }}>
                          {percentage}%
                        </span>
                      </div>
                    );
                  }
                }
              ]}
              style={{ 
                background: '#fff'
              }}
            />
          </Card>
        </Col>
      </Row>

      {/* 底部操作區（參考示意） */}
      <Card bordered={false} bodyStyle={{ textAlign:'center' }}>
        <Space>
          {/*<Button type="primary">WMS處理訂單</Button>*/}
          <Button type="primary">全部訂單</Button>
          <Button>同步狀態</Button>
          {/*<Button danger>反選</Button>*/}
        </Space>
      </Card>
    </Space>
  );
};

export default HKTVmallDashboards;


